import React, { forwardRef, HTMLAttributes } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

// Base DataRow for lists and tables
interface DataRowProps extends HTMLAttributes<HTMLDivElement> {
  isClickable?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
  showChevron?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'card' | 'compact';
}

export const DataRow = forwardRef<HTMLDivElement, DataRowProps>(
  (
    {
      children,
      isClickable = false,
      isSelected = false,
      isHovered = false,
      showChevron = false,
      disabled = false,
      variant = 'default',
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const baseStyles = `
      relative flex items-center
      transition-all duration-150 ease-out
    `;

    const variantStyles = {
      default: 'px-4 py-3 border-b border-white/5',
      card: 'px-4 py-4 rounded-xl bg-slate-900/30 border border-white/5',
      compact: 'px-3 py-2 border-b border-white/5',
    };

    const interactiveStyles = isClickable && !disabled
      ? `
        cursor-pointer
        hover:bg-white/[0.03]
        active:bg-white/[0.05]
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-inset
      `
      : '';

    const selectedStyles = isSelected
      ? 'bg-blue-500/10 border-l-2 border-l-blue-500'
      : '';

    const disabledStyles = disabled
      ? 'opacity-50 cursor-not-allowed'
      : '';

    return (
      <div
        ref={ref}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable && !disabled ? 0 : undefined}
        onClick={!disabled ? onClick : undefined}
        onKeyDown={(e) => {
          if (isClickable && !disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        className={cn(
          baseStyles,
          variantStyles[variant],
          interactiveStyles,
          selectedStyles,
          disabledStyles,
          className
        )}
        {...props}
      >
        <div className="flex-1 flex items-center gap-3 min-w-0">
          {children}
        </div>
        {showChevron && isClickable && (
          <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
        )}
      </div>
    );
  }
);

DataRow.displayName = 'DataRow';

// Table Row wrapper for AG-Grid or native tables
interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isClickable?: boolean;
  isSelected?: boolean;
  isHovered?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  (
    {
      children,
      isClickable = false,
      isSelected = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    return (
      <tr
        ref={ref}
        onClick={onClick}
        className={cn(
          'transition-colors duration-150',
          isClickable && 'cursor-pointer hover:bg-white/[0.03] active:bg-white/[0.05]',
          isSelected && 'bg-blue-500/10',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }
);

TableRow.displayName = 'TableRow';

// Card-style list item
interface ListItemProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  subtitle?: string;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  isClickable?: boolean;
  isSelected?: boolean;
  showChevron?: boolean;
  status?: 'default' | 'success' | 'warning' | 'error' | 'info';
}

export const ListItem = forwardRef<HTMLDivElement, ListItemProps>(
  (
    {
      title,
      subtitle,
      leftContent,
      rightContent,
      isClickable = true,
      isSelected = false,
      showChevron = true,
      status = 'default',
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const statusBorder = {
      default: '',
      success: 'border-l-2 border-l-emerald-500',
      warning: 'border-l-2 border-l-amber-500',
      error: 'border-l-2 border-l-red-500',
      info: 'border-l-2 border-l-blue-500',
    };

    return (
      <DataRow
        ref={ref}
        variant="card"
        isClickable={isClickable}
        isSelected={isSelected}
        showChevron={showChevron}
        onClick={onClick}
        className={cn(statusBorder[status], className)}
        {...props}
      >
        {leftContent && (
          <div className="flex-shrink-0">{leftContent}</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{title}</p>
          {subtitle && (
            <p className="text-xs text-slate-500 truncate mt-0.5">{subtitle}</p>
          )}
        </div>
        {rightContent && (
          <div className="flex-shrink-0 ml-3">{rightContent}</div>
        )}
      </DataRow>
    );
  }
);

ListItem.displayName = 'ListItem';

// Activity/Timeline Item
interface ActivityItemProps extends HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode;
  iconBg?: string;
  title: string;
  description?: string;
  timestamp?: string;
  action?: React.ReactNode;
  isLast?: boolean;
}

export const ActivityItem = forwardRef<HTMLDivElement, ActivityItemProps>(
  (
    {
      icon,
      iconBg = 'bg-slate-800',
      title,
      description,
      timestamp,
      action,
      isLast = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'relative flex gap-4 pb-6',
          !isLast && 'after:absolute after:left-5 after:top-10 after:bottom-0 after:w-px after:bg-white/10',
          className
        )}
        {...props}
      >
        {/* Icon */}
        <div className={cn(
          'relative z-10 flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border border-white/10',
          iconBg
        )}>
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white">{title}</p>
              {description && (
                <p className="text-xs text-slate-500 mt-0.5">{description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {timestamp && (
                <span className="text-xs text-slate-600">{timestamp}</span>
              )}
              {action}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ActivityItem.displayName = 'ActivityItem';

// Selectable Card (for grid layouts)
interface SelectableCardProps extends HTMLAttributes<HTMLDivElement> {
  isSelected?: boolean;
  isDisabled?: boolean;
  children: React.ReactNode;
}

export const SelectableCard = forwardRef<HTMLDivElement, SelectableCardProps>(
  (
    {
      isSelected = false,
      isDisabled = false,
      children,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        onClick={!isDisabled ? onClick : undefined}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick?.(e as any);
          }
        }}
        className={cn(
          'relative p-4 rounded-xl border transition-all duration-150',
          'cursor-pointer',
          'hover:border-white/20 hover:bg-white/[0.02]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50',
          isSelected
            ? 'border-blue-500/50 bg-blue-500/5 ring-1 ring-blue-500/20'
            : 'border-white/10 bg-slate-900/30',
          isDisabled && 'opacity-50 cursor-not-allowed hover:border-white/10 hover:bg-transparent',
          className
        )}
        {...props}
      >
        {/* Selection indicator */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {children}
      </div>
    );
  }
);

SelectableCard.displayName = 'SelectableCard';

export default DataRow;
